import { MoneyText } from '@/components/MoneyText';
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { PriceBand, PriceBook } from '@/api/entitlements';
import type { Tier } from '@/api/me';
import { formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

interface PriceBookBlockProps {
  prices: PriceBook;
  tier: Tier;
}

function bandLabel(band: PriceBand, t: TFunction): string {
  if (band.to === null) return t('prices.bandOpen', { from: band.from });
  return t('prices.band', { from: band.from, to: band.to });
}

/** TMMAPP-0135 item 7: the label says what the band charges, one catalog string per case. */
function bandAmount(band: PriceBand, currency: string, locale: string, t: TFunction): string {
  const flat = formatMoneyMinor(band.flatMinor, currency, locale);
  const per = formatMoneyMinor(band.perUnitMinor, currency, locale);
  if (band.flatMinor > 0 && band.perUnitMinor > 0)
    return t('prices.flatPlusPerSeat', { flat, per });
  if (band.flatMinor > 0) return t('prices.flatPerMonth', { flat });
  if (band.perUnitMinor > 0) return t('prices.perSeat', { amount: per });
  return t('prices.included');
}

function PriceRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: { xs: 'block', md: 'table-row' },
        py: { xs: 1, md: 0 },
      }}
    >
      <Typography
        variant="body2"
        component="dt"
        sx={{
          display: { xs: 'block', md: 'table-cell' },
          color: 'text.secondary',
          paddingInlineEnd: { md: 3 },
          py: { md: 0.75 },
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        component="dd"
        sx={{
          display: { xs: 'block', md: 'table-cell' },
          m: 0,
          py: { md: 0.75 },
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function PriceBookBlock({ prices, tier }: PriceBookBlockProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const currency = prices.currency;
  const trialDays =
    tier === 'GUEST' ? prices.trialDaysGuestConversion : prices.trialDaysNewSubscriber;

  return (
    <Box
      component="dl"
      sx={{
        display: { xs: 'block', md: 'table' },
        m: 0,
        width: '100%',
      }}
    >
      <PriceRow
        label={t('prices.base')}
        value={<MoneyText minor={prices.baseMinor} currency={currency} />}
      />
      {prices.seatBands.map((band) => (
        <PriceRow
          key={`seat-${band.from}-${band.to ?? 'open'}`}
          label={`${t('prices.seats')} · ${bandLabel(band, t)}`}
          value={bandAmount(band, currency, locale, t)}
        />
      ))}
      {prices.connectedAccountBands.map((band) => (
        <PriceRow
          key={`conn-${band.from}-${band.to ?? 'open'}`}
          label={`${t('prices.connectedAccounts')} · ${bandLabel(band, t)}`}
          value={bandAmount(band, currency, locale, t)}
        />
      ))}
      {/* AI is prepaid (TMMAPI-0151, MM-D-131): the first purchase enables it. */}
      <PriceRow
        label={t('prices.aiPrepaid', {
          amount: formatMoneyMinor(prices.aiFirstPurchaseMinor, currency, locale),
        })}
        value=""
      />
      {trialDays > 0 && <PriceRow label={t('prices.trial', { count: trialDays })} value="" />}
      {/* Prices are pre-tax (MM-D-139): Stripe Tax calculates it on each charge. */}
      <PriceRow label={t('prices.taxAtCheckout')} value="" />
    </Box>
  );
}
